import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Plus, Trash2, Sparkles, RefreshCw, Users, Briefcase, HelpCircle, MessageSquare, Image as ImageIcon, MapPin, Trophy, Newspaper, Wand2 } from "lucide-react";

/**
 * Preset-Library: Titel + Briefing-Text + Icon.
 * Der User kann jedes Briefing nach Klick noch frei anpassen.
 */
export const SUBPAGE_PRESETS = [
  { key: "team", title: "Team", icon: Users, briefing: "Erstelle eine Team-Seite. Die Hero-Sektion zeigt die Team-Energie und den Zusammenhalt. Die Features-Sektion präsentiert 3-6 Mitarbeiter (jeweils Name, Rolle, 1-Satz-Bio passend zur Branche). Content-Block erklärt die Team-Philosophie. CTA führt zur Kontakt-/Karriereseite." },
  { key: "karriere", title: "Karriere", icon: Briefcase, briefing: "Erstelle eine Karriere-Seite. Hero macht Lust aufs Bewerben. Stats zeigen Mitarbeiter-Zahlen, Ausbildungsjahre oder Weiterbildungen. Features sind 3 Gründe warum hier arbeiten. Content-Block listet 2-3 offene Stellen. CTA = Initiativbewerbung senden." },
  { key: "faq", title: "FAQ", icon: HelpCircle, briefing: "Erstelle eine FAQ-Seite. Hero fängt die häufigsten Zweifel auf. Stats zeigen Service-Metriken. Features sind 6 typische Fragen mit kurzen Antworten (Title=Frage, Description=Antwort), branchenspezifisch. Content erklärt warum Fragen wichtig sind. CTA = persönliche Beratung." },
  { key: "testimonials", title: "Kundenstimmen", icon: MessageSquare, briefing: "Erstelle eine Testimonials-Seite. Hero feiert die Kundenliebe. Stats zeigen Zufriedenheit (98% o.ä.), Empfehlungen, Sterne. Features sind 5 authentische Kundenstimmen (Titel=Kundenname+Rolle, Description=1-2 Sätze Zitat, branchenspezifisch). Content beschreibt Kundenbeziehung. CTA = selber Kunde werden." },
  { key: "galerie", title: "Galerie", icon: ImageIcon, briefing: "Erstelle eine Galerie-Übersichtsseite. Hero macht Lust auf visuelle Qualität. Stats zeigen Projekt-/Arbeiten-Zahlen. Features sind 6 Bildkategorien (Titel=Kategorie-Name, Description=kurze Beschreibung), branchenspezifisch. Content erklärt die Qualität. CTA = Arbeit anfragen." },
  { key: "standorte", title: "Standorte", icon: MapPin, briefing: "Erstelle eine Standorte-Übersicht für mehrere Filialen. Hero betont die Nähe zum Kunden. Stats = Standort-Anzahl, Jahre, Öffnungstage. Features sind 3-4 Standorte (Titel=Stadt, Description=Adresse+kurze Info). Content-Block erklärt Expansion. CTA = nächsten Standort finden." },
  { key: "historie", title: "Unsere Geschichte", icon: Trophy, briefing: "Erstelle eine Historie-/Meilensteine-Seite. Hero erzählt den Ursprung. Stats sind markante Zahlen (Gründungsjahr, Mitarbeiter-Wachstum, Projektanzahl). Features sind 4 Meilensteine (Titel=Jahreszahl+Ereignis, Description=was passierte und warum wichtig). Content-Block beschreibt die Werte heute. CTA = Teil der nächsten Kapitel werden." },
  { key: "blog", title: "Blog", icon: Newspaper, briefing: "Erstelle eine Blog-Übersichtsseite. Hero positioniert als Quelle für Insights. Stats = veröffentlichte Artikel, Leser, Kategorien. Features sind 3 Beispiel-Blogpost-Previews (Titel=Post-Titel, Description=1-Satz-Teaser). Content erklärt Themen-Fokus. CTA = Newsletter abonnieren." },
];

/**
 * Props:
 *   subpages: [{title, briefing, preset_key?}]   // current array
 *   setSubpages: (arr) => void
 *   onGenerateAll?: () => Promise<void>          // if set → shows "Alle generieren" button (Builder-only)
 *   generating?: boolean
 *   compact?: boolean                             // project-create modal uses this (smaller)
 */
export const SubpagesEditor = ({ subpages = [], setSubpages, onGenerateAll, generating = false, compact = false }) => {
  const [expandedIdx, setExpandedIdx] = useState(compact ? -1 : 0);

  const addFromPreset = (preset) => {
    const copy = [...subpages, { title: preset.title, briefing: preset.briefing, preset_key: preset.key }];
    setSubpages(copy);
    setExpandedIdx(copy.length - 1);
  };

  const addEmpty = () => {
    const copy = [...subpages, { title: "", briefing: "", preset_key: "custom" }];
    setSubpages(copy);
    setExpandedIdx(copy.length - 1);
  };

  const update = (i, patch) => {
    setSubpages(subpages.map((s, ix) => (ix === i ? { ...s, ...patch } : s)));
  };

  const remove = (i) => {
    setSubpages(subpages.filter((_, ix) => ix !== i));
    if (expandedIdx === i) setExpandedIdx(-1);
  };

  return (
    <div className="space-y-3" data-testid="subpages-editor">
      {/* PRESET CHIPS */}
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Schnellstart-Vorlagen</Label>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUBPAGE_PRESETS.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => addFromPreset(p)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-sm border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid={`preset-${p.key}`}
              >
                <Icon size={11} /> {p.title}
              </button>
            );
          })}
          <button
            type="button"
            onClick={addEmpty}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-sm border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors"
            data-testid="preset-custom"
          >
            <Plus size={11} /> Eigene Seite
          </button>
        </div>
      </div>

      {/* LIST */}
      {subpages.length === 0 && (
        <div className="p-4 text-center border border-dashed border-border rounded-sm">
          <p className="text-xs text-muted-foreground">Noch keine Unterseiten geplant. Klicke oben auf eine Vorlage oder „Eigene Seite".</p>
        </div>
      )}

      {subpages.map((s, i) => {
        const expanded = expandedIdx === i;
        return (
          <div key={i} className="border border-border rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setExpandedIdx(expanded ? -1 : i)}
              className="w-full flex items-center justify-between px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              data-testid={`subpage-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Wand2 size={11} className="text-primary shrink-0" />
                <span className="text-xs font-semibold truncate">{s.title || "(Titel fehlt)"}</span>
                {s.preset_key && s.preset_key !== "custom" && (
                  <span className="text-[10px] text-muted-foreground bg-background px-1.5 py-0.5 rounded-sm shrink-0">{s.preset_key}</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); remove(i); } }}
                  className="text-muted-foreground hover:text-destructive p-1 cursor-pointer"
                  data-testid={`remove-subpage-${i}`}
                >
                  <Trash2 size={11} />
                </span>
              </div>
            </button>
            {expanded && (
              <div className="p-3 space-y-2 bg-background">
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Titel (erscheint in der Nav)</Label>
                  <Input
                    value={s.title}
                    onChange={(e) => update(i, { title: e.target.value })}
                    placeholder="z.B. Team, Karriere, FAQ"
                    className="rounded-sm text-sm h-8 mt-1"
                    data-testid={`subpage-title-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Briefing an die KI</Label>
                  <Textarea
                    value={s.briefing}
                    onChange={(e) => update(i, { briefing: e.target.value })}
                    rows={compact ? 3 : 5}
                    placeholder="Was soll drin stehen? Welche Abschnitte? Welcher Ton? Zielgruppe?"
                    className="rounded-sm text-xs mt-1"
                    data-testid={`subpage-briefing-${i}`}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1 italic">Tipp: Konkret werden – z.B. „6 Stylisten mit Specialty, freundlicher Ton, Ziel Bewerbungen".</p>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* GENERATE ALL BUTTON (Builder only) */}
      {onGenerateAll && subpages.length > 0 && (
        <div className="pt-1">
          <Button
            type="button"
            onClick={onGenerateAll}
            disabled={generating}
            className="w-full rounded-sm text-sm gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-90"
            data-testid="generate-subpages-btn"
          >
            {generating ? <><RefreshCw size={13} className="animate-spin" /> KI generiert {subpages.length} Seite{subpages.length !== 1 ? "n" : ""}...</> : <><Sparkles size={13} /> Alle {subpages.length} Seite{subpages.length !== 1 ? "n" : ""} mit KI generieren</>}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1 italic text-center">Neue Seiten werden an bestehende angehängt. Kann danach einzeln im Editor weiter angepasst werden.</p>
        </div>
      )}
    </div>
  );
};
